import { z } from "zod";
import { COMPASS_ROLE_EMAIL_ERROR, isCompassRoleEmailAllowed } from "../compassRole";
import { NormalizedEmail } from "../email";
import { TipTapDocSchema } from "../tiptapDoc";
import { CurrentUser, Password, Role, SocialProfile, User, UserWithFriend } from "../user";

// PRTL-1334 — reject assigning a Compass role alongside a non-Compass email at
// the API boundary. `email` is always present here, so this fully covers the
// "set a Compass role" case; the "change an existing Compass user's email"
// case (role omitted) is enforced against the persisted user in the router.
// PRTL-1348 review: `name` and `organizationId` were dead fields — no client
// ever sent them, and spreading them into prisma.user.update was a runtime
// crash waiting to happen (neither is a User column any more). Org changes
// happen through the admin panel's membership flows.
const CreateUpdateUserInput = z
    .object({
        email: NormalizedEmail,
        role: Role.optional().nullish(),
    })
    .refine((data) => isCompassRoleEmailAllowed(data.role, data.email), {
        message: COMPASS_ROLE_EMAIL_ERROR,
        path: ["role"],
    });

export const schema = {
    me: {
        input: z.void(),
        output: CurrentUser,
    },
    list: {
        input: z.object({
            search: z.coerce.string().min(0).max(100).optional(),
            page: z.number().min(0).optional(),
            pageSize: z.number().min(1).max(30).optional(),
            sortBy: z.enum(["name", "email"]).optional(),
            sort: z.enum(["asc", "desc"]).optional(),
            organizationId: z.number().optional(),
        }),
        output: z.object({
            users: z.array(User),
            totalFound: z.number(),
            total: z.number(),
        }),
    },
    toggleFollow: {
        input: z.object({
            userId: z.number(),
        }),
        output: z.object({
            message: z.string(),
        }),
    },
    getPublic: {
        input: z.object({
            id: z.number(),
        }),
        output: SocialProfile,
    },
    get: {
        input: z.object({
            id: z.number(),
        }),
        output: User,
    },
    adminUpdate: {
        input: z.object({
            id: z.number(),
            user: CreateUpdateUserInput,
        }),
        output: z.void(),
    },
    getDeclineRequest: {
        input: z.void(),
        output: z.array(z.number()),
    },
    getAll: {
        input: z
            .object({
                search: z.string().optional(),
            })
            .optional(),
        output: z.array(UserWithFriend).nullable(),
    },
    uploadAvatar: {
        input: z.object({
            contentType: z.enum(["image/jpeg", "image/jpg", "image/png"]),
        }),
        output: z.object({
            fileId: z.string(),
            uploadUrl: z.string(),
        }),
    },
    completeAvatarUpload: {
        input: z.object({
            fileId: z.string(),
        }),
        output: z.void(),
    },
    updateEmail: {
        input: z.object({
            email: NormalizedEmail,
        }),
        output: z.object({
            token: z.string(),
        }),
    },
    updatePassword: {
        input: z.object({
            currentPassword: z.string().min(1),
            newPassword: Password,
        }),
        output: z.void(),
    },
    updateBio: {
        input: z.object({
            bio: z.string(),
        }),
        output: z.void(),
    },
    updateAboutMe: {
        input: z.object({
            /** Legacy plaintext input — kept so navigator continues working
             *  during the migration. Server prefers `aboutMeContentJson`. */
            aboutMe: z.string().optional(),
            aboutMeContentJson: TipTapDocSchema.nullable().optional(),
        }),
        output: z.void(),
    },
    updateOrganization: {
        input: z.object({
            organization: z.string(),
        }),
        output: z.void(),
    },
    requestSent: {
        input: z.object({
            userId: z.number(),
        }),
        output: z.void(),
    },
    acceptRequest: {
        input: z.object({
            id: z.number(),
            status: z.enum(["DECLINED", "ACCEPTED"]),
        }),
        output: z.void(),
    },
    deleteRequest: {
        input: z.object({
            id: z.number(),
        }),
        output: z.void(),
    },
    deleteAvatar: {
        input: z.void(),
        output: z.object({
            success: z.boolean(),
            message: z.string(),
        }),
    },
};
